import { useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useLogin } from "@/hooks/mutations/use-login";
import { useAuthInfo } from "@/hooks/use-auth-info";
import { ThemeSwitcher } from "../theme-switcher";
import { Button } from "../ui/button";
import {
	Card,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export function Login() {
	const [state, setState] = useState<"prompt" | "input">("prompt");
	const navigate = useNavigate();
	const { mutateAsync: login } = useLogin();
	const emailRef = useRef<HTMLInputElement | null>(null);
	const passwordRef = useRef<HTMLInputElement | null>(null);
	const { user, isAuthed } = useAuthInfo();

	const handleLogin = async () => {
		if (!emailRef.current || !passwordRef.current) {
			return;
		}

		try {
			await login(
				{
					email: emailRef.current.value,
					password: passwordRef.current.value,
				},
				{
					onSuccess: () => {
						navigate("/dashboard");
					},
				},
			);
		} catch {
			console.error("Login failed");
		}
	};

	if (user && isAuthed) {
		return <Navigate to="/dashboard" />;
	}

	return (
		<>
			<div className="fixed top-4 right-4 z-50">
				<ThemeSwitcher />
			</div>
			<Card className="relative w-full max-w-sm overflow-hidden pt-0">
				<div className="bg-primary absolute inset-0 z-30 aspect-video opacity-50 mix-blend-color" />
				<img
					src="https://images.unsplash.com/photo-1604076850742-4c7221f3101b?q=80&w=1887&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
					alt="Photo by mymind on Unsplash"
					title="Photo by mymind on Unsplash"
					className="relative z-20 aspect-video w-full object-cover brightness-60 grayscale"
				/>
				{state === "prompt" && (
					<>
						<CardHeader>
							<CardTitle>IED Administrative Dashboard</CardTitle>
							<CardDescription>
								A centralized place to manage our technical products and
								infrastructure
							</CardDescription>
						</CardHeader>
						<CardFooter>
							<Button onClick={() => setState("input")}>Login</Button>
						</CardFooter>
					</>
				)}
				{state === "input" && (
					<>
						<CardHeader className="space-y-4">
							<CardTitle>Enter your credentials</CardTitle>
							<div className="space-y-4">
								<div className="space-y-2">
									<Label htmlFor="email">Email</Label>
									<Input ref={emailRef} id="email" />
								</div>
								<div className="space-y-2">
									<Label htmlFor="password">Password</Label>
									<Input type="password" ref={passwordRef} id="password" />
								</div>
							</div>
						</CardHeader>
						<CardFooter className="flex justify-between">
							<Button variant="outline" onClick={() => setState("prompt")}>
								Back
							</Button>
							<Button onClick={handleLogin}>Login</Button>
						</CardFooter>
					</>
				)}
			</Card>
		</>
	);
}
